import { useMemo } from 'react';

import { Button } from '@/components/design-system';
import {
  COLOR_SCALE,
  FONT_WEIGHTS,
  MARGIN_BOTTOM,
  RADIUS_SCALE,
  SPACE_Y_SCALE,
  SURFACE_PADDING,
  TYPE_SCALE,
  MARGIN_TOP,
  SIZE_SCALE,
  WORD_BREAK,
} from '@/components/design-system/tokens';
import { useTranslation } from '@/hooks/useTranslation';
import { formatDateTime } from '@/utils';

interface ErrorBoundaryDisplayProps {
  error: Error | null | undefined;
  onRetry?: () => void;
  onHardRefresh?: () => void;
  supportId?: string;
  timestamp: string | Date | null | undefined;
}

const isDev = import.meta.env.DEV || import.meta.env.MODE === 'development';

/**
 * Get formatted timestamp for display
 */
function getFormattedTimestamp(timestamp: string | Date | null | undefined): string {
  const value = timestamp ?? new Date();
  return formatDateTime(value, {
    locale: 'en-US',
    formatOptions: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    },
  });
}

/**
 * Check if error is an asset loading error
 */
function isAssetLoadingError(error: Error | null | undefined): boolean {
  if (!error?.message) return false;
  return (
    error.message.includes('Failed to load component') ||
    error.message.includes('404') ||
    error.message.includes('Failed to fetch')
  );
}

/**
 * Get error message for display
 */
function getErrorMessage(
  error: Error | null | undefined,
  t: (namespace: string, key: string, fallback: string) => string
): string {
  return error?.message ?? t('error_boundary', 'generic_error_message', 'Something went wrong');
}

/**
 * Get retry handler function
 */
function getRetryHandler(onRetry?: () => void) {
  return (
    onRetry ??
    (() => {
      if (typeof window !== 'undefined') {
        window.location.reload();
      }
    })
  );
}

/**
 * Get hard refresh handler function
 */
function getHardRefreshHandler(onHardRefresh?: () => void) {
  return (
    onHardRefresh ??
    (() => {
      if (typeof window !== 'undefined') {
        window.location.href = window.location.href + '?refresh=' + Date.now();
      }
    })
  );
}

export default function ErrorBoundaryDisplay({
  error,
  onRetry,
  onHardRefresh,
  supportId,
  timestamp,
}: ErrorBoundaryDisplayProps) {
  const { t } = useTranslation();

  const parsedTimestamp = useMemo(() => getFormattedTimestamp(timestamp), [timestamp]);
  const errorMessage = getErrorMessage(error, t);
  const isAssetError = isAssetLoadingError(error);
  const handleRetryClick = getRetryHandler(onRetry);
  const handleHardRefresh = getHardRefreshHandler(onHardRefresh);

  return (
    <div
      className={`min-h-screen bg-muted/50 flex items-center justify-center ${SURFACE_PADDING['4']}`}
    >
      <div
        className={`max-w-md w-full bg-background ${RADIUS_SCALE.lg} shadow-lg ${SURFACE_PADDING['6']} text-center border border-border`}
      >
        <div
          className={`${SIZE_SCALE['16']} bg-destructive/10 ${RADIUS_SCALE.full} flex items-center justify-center mx-auto ${MARGIN_BOTTOM['4']}`}
        >
          <svg
            className={`${SIZE_SCALE['8']} text-destructive`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>
        <h2
          className={`${TYPE_SCALE.xl} ${FONT_WEIGHTS.semibold} text-foreground ${MARGIN_BOTTOM['2']}`}
        >
          {t('error_boundary', 'page_loading_error', 'Page Loading Error')}
        </h2>
        <p className={`text-muted-foreground ${MARGIN_BOTTOM['4']}`}>
          {isAssetError
            ? t(
                'error_boundary',
                'caching_issue',
                'This appears to be a caching issue. The page assets may be outdated.'
              )
            : t(
                'error_boundary',
                'network_issue',
                'There was an issue loading this page. This might be due to a network issue.'
              )}
        </p>

        <div
          className={`text-left ${TYPE_SCALE.sm} text-muted-foreground ${MARGIN_BOTTOM['4']} ${SPACE_Y_SCALE['1']}`}
        >
          <p>
            <span className={`${FONT_WEIGHTS.medium} text-foreground`}>
              {t('error_boundary', 'timestamp_label', 'Timestamp')}:
            </span>{' '}
            {parsedTimestamp}
          </p>
          <p className={WORD_BREAK['break-words']}>
            <span className={`${FONT_WEIGHTS.medium} ${COLOR_SCALE['foreground-muted']}`}>
              {t('error_boundary', 'error_message_label', 'Error')}:
            </span>{' '}
            {errorMessage}
          </p>
          {supportId && (
            <p>
              <span className={`${FONT_WEIGHTS.medium} ${COLOR_SCALE['foreground-muted']}`}>
                {t('error_boundary', 'support_id_label', 'Support ID')}:
              </span>{' '}
              {supportId}
            </p>
          )}
        </div>

        <div className={SPACE_Y_SCALE['2']}>
          <Button
            onClick={handleRetryClick}
            variant="primary"
            fullWidth
            className=""
            aria-label={t('error_boundary', 'clear_cache_refresh', 'Clear Cache & Refresh')}
          >
            {t('error_boundary', 'clear_cache_refresh', 'Clear Cache & Refresh')}
          </Button>
          <Button
            onClick={handleHardRefresh}
            variant="secondary"
            fullWidth
            className=""
            aria-label={t('error_boundary', 'hard_refresh', 'Hard Refresh (Clear All)')}
          >
            {t('error_boundary', 'hard_refresh', 'Hard Refresh (Clear All)')}
          </Button>
          <Button
            onClick={() => (window.location.href = '/')}
            variant="ghost"
            fullWidth
            className={`${COLOR_SCALE['surface-subtle']} ${COLOR_SCALE['foreground-muted']} hover:${COLOR_SCALE['surface-muted']}`}
            aria-label={t('error_boundary', 'go_home', 'Go to Home')}
          >
            {t('error_boundary', 'go_home', 'Go to Home')}
          </Button>
        </div>

        {isDev && (
          <details className={`${MARGIN_TOP['4']} text-left`}>
            <summary
              className={`cursor-pointer ${TYPE_SCALE.sm} ${COLOR_SCALE['foreground-muted']}`}
            >
              {t('error_boundary', 'error_details', 'Error Details')}
            </summary>
            <pre
              className={`${TYPE_SCALE.xs} ${COLOR_SCALE['foreground-muted']} ${MARGIN_TOP['2']} overflow-auto whitespace-pre-wrap break-words`}
            >
              {error?.stack ?? errorMessage}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}
